import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import timeouts from '../../../_shared/constants/timeouts';
import StyledHistory, {
  StyledHistoryHeading,
  StyledHistorySubHeading,
  StyledBox,
  StyledHistoryGroupings,
  StyledHistoryTitle,
  StyledHistoryButton,
  HistoryItemContainer,
  HistoryType,
  StyledHistoryDisclosuresNone,
  StyledDivider,
  StyledDisclosureCountBadge,
  StyledDisclosures,
  StyledDisclosuresLabel,
  StyledDisclosureIcon
} from './Styles';

import logocarfax from '../../../assets/external/logo-carfax.jpg';
import logoautocheck from '../../../assets/external/logo-autocheck.jpg';
import Loading from '../../../_shared/components/Loading';
import Alert from '../../../_shared/components/Alert';
import TRToast from '../../../_shared/helpers/TRToast';

const propTypes = {
  historyList: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string
    })
  )
};

const defaultProps = {
  historyList: []
};

const History = ({
  historyList,
  histDisclosureCount,
  historyReportLink,
  countryId,
  isLoading,
  hasErrors,
  viewVehicleHistoryReport
}) => {
  const openVhrReport = () => {
    if (historyReportLink) {
      viewVehicleHistoryReport();
      window.open(historyReportLink, '_blank');
    }
  };

  let reportType = 'CarFax';
  let reportLogo = logocarfax;
  if (countryId === 3) {
    reportType = 'AutoCheck';
    reportLogo = logoautocheck;
  }

  useEffect(() => {
    if (hasErrors) {
      TRToast(
        <Alert
          variant="error"
          messages={[<FormattedMessage id="vdp.error.general" />]}
        />,
        { autoClose: timeouts.ERROR }
      );
    }
  }, [hasErrors]);

  return isLoading || hasErrors ? (
    <StyledHistory id="history" data-testid="vdpHistoryLoading">
      <StyledBox>
        <StyledHistoryHeading>
          <Loading width="300px" height="30px" />
        </StyledHistoryHeading>
      </StyledBox>
      <StyledHistoryGroupings>
        <Loading width="500px" height="200px" />
      </StyledHistoryGroupings>
    </StyledHistory>
  ) : (
    <StyledHistory id="history" data-testid="vdpHistory">
      <StyledBox>
        <StyledHistoryHeading
          id="vdpHistoryTitle"
          data-testid="vdpHistory-title"
        >
          {/* History summary */}
          <FormattedMessage id="vdp.history.title" />
        </StyledHistoryHeading>
        {histDisclosureCount > 0 && (
          <StyledDisclosureCountBadge>
            {histDisclosureCount}
          </StyledDisclosureCountBadge>
        )}
        <StyledHistorySubHeading>
          {/* Always view the {reportType} report before bidding. */}
          <FormattedMessage
            id="vdp.history.alwaysViewReport"
            values={{ reportType }}
          />
        </StyledHistorySubHeading>
        {historyReportLink && (
          <StyledHistoryButton
            onClick={openVhrReport}
            enabled={historyReportLink}
          >
            {/* VIEW */}
            <FormattedMessage id="vdp.history.viewButton" />
            <StyledDisclosureIcon src={reportLogo} alt="{reportType}" />
          </StyledHistoryButton>
        )}
      </StyledBox>
      <StyledHistoryGroupings>
        {historyList.map((history, id) => (
          <StyledHistoryTitle key={history.id}>
            <HistoryItemContainer data-testid={`vdpHistory-${history.id}`}>
              <HistoryType>{history.type}</HistoryType>
              {!history.disclosures.length && (
                <StyledHistoryDisclosuresNone className="notranslate">
                  {/* None reported */}
                  <FormattedMessage id="vdp.history.noneReported" />
                </StyledHistoryDisclosuresNone>
              )}
            </HistoryItemContainer>
            {history.disclosures && history.disclosures.length > 0 && (
              <StyledDisclosures>
                {history.disclosures.map(disc => {
                  return (
                    <StyledDisclosuresLabel key={disc}>
                      {disc}
                    </StyledDisclosuresLabel>
                  );
                })}
              </StyledDisclosures>
            )}
            {id !== historyList.length - 1 && (
              <StyledDivider className="disclosureDivider" />
            )}
          </StyledHistoryTitle>
        ))}
      </StyledHistoryGroupings>
    </StyledHistory>
  );
};

History.prototype = propTypes;
History.defaultProps = defaultProps;

export default History;
